import type { TemplateFormStatus } from '@common/enums';
import type { EmailTemplate } from '@common/types/lawfirms';
import type {
    ApsPrefillTagApplyResponse,
    ApsPrefillTagPreview,
    QuoteTemplate,
    QuoteTemplateAttachment,
    QuoteTemplateAttachmentsUpdate,
    QuoteTemplateCreate,
    QuoteTemplateDefaultRetainerUpdate,
    QuoteTemplateEntry,
    QuoteTemplatePricingUpdate,
    QuoteTemplateResponse,
    QuoteTemplateUpdate,
    TemplateFormWithFields,
} from '@common/types/quoteTemplates';
import type { Operation } from 'fast-json-patch';
import axiosInstance from '@/services/axiosInstance';

export type DynamicPriceCategoryType = 'LegalFee' | 'DisbursementFee' | 'RegistrationFee' | '';

export interface DynamicPriceType {
    quoteTemplateDynamicPriceId?: number;
    quoteTemplateId?: number;
    dynamicPriceCategory: DynamicPriceCategoryType;
    question: string;
    answer: string;
    description: string;
    amount: number;
    createdAt?: string;
    modifiedAt?: string;
    createdBy?: number;
    modifiedBy?: number;
}

export interface IntakeDesignPayload {
    createFormManually?: boolean;
    createByReference?: boolean;
    copyFromExisting?: boolean;
    referencedQuoteTemplateId?: number | null;
}

export interface DynamicFeesPayload {
    dynamicPriceCategory: DynamicPriceCategoryType;
    question: string;
    answer: string;
    description: string;
    amount: number;
}

export async function createQuoteTemplate(payload: QuoteTemplateCreate) {
    return axiosInstance.post<TemplateFormWithFields>('/api/v1/quote-templates', payload);
}

export async function fetchApsPrefillTags(quoteTemplateId: number) {
    return axiosInstance.get<ApsPrefillTagPreview>(
        `/api/v1/quote-templates/${quoteTemplateId}/aps-prefill-tags`,
    );
}

export async function applyApsPrefillTags(
    quoteTemplateId: number,
    mappings: Array<{ templateFormFieldId: string; tag: string }>,
) {
    return axiosInstance.post<ApsPrefillTagApplyResponse>(
        `/api/v1/quote-templates/${quoteTemplateId}/aps-prefill-tags`,
        { mappings },
    );
}

export async function updateQuoteTemplate(quoteTemplateId: number, payload: QuoteTemplateUpdate) {
    return axiosInstance.put<TemplateFormWithFields>(
        `/api/v1/quote-templates/${quoteTemplateId}`,
        payload,
    );
}

export async function fetchQuoteTemplateById(quoteTemplateId: number | string) {
    return axiosInstance.get<QuoteTemplateResponse>(`/api/v1/quote-templates/${quoteTemplateId}`);
}

export async function fetchQuoteTemplateAttachments(quoteTemplateId: number) {
    return axiosInstance.get<QuoteTemplateAttachment[]>(
        `/api/v1/quote-templates/${quoteTemplateId}/attachments`,
    );
}

export async function updateQuoteTemplateAttachments(
    quoteTemplateId: number,
    payload: QuoteTemplateAttachmentsUpdate,
) {
    return axiosInstance.put<QuoteTemplateAttachment[]>(
        `/api/v1/quote-templates/${quoteTemplateId}/attachments`,
        payload,
    );
}

export async function updateQuoteTemplateDefaultRetainer(
    quoteTemplateId: number,
    payload: QuoteTemplateDefaultRetainerUpdate,
) {
    return axiosInstance.put<QuoteTemplateDefaultRetainerUpdate>(
        `/api/v1/quote-templates/${quoteTemplateId}/default-retainer`,
        payload,
    );
}

export async function fetchQuoteTemplates(lawFirmId: number) {
    return axiosInstance.get<QuoteTemplateEntry[]>(
        `/api/v1/quote-templates/${lawFirmId}/quotetemplates`,
    );
}

export async function createIntakeByReference(
    quoteTemplateId: number,
    payload: IntakeDesignPayload,
) {
    return axiosInstance.post<TemplateFormWithFields>(
        `/api/v1/quote-templates/${quoteTemplateId}/template-forms/by-reference`,
        payload,
    );
}

export async function updateQuoteIntakeTemplateStatus(
    quoteTemplateId: number | string,
    templateFormId: string,
    status: TemplateFormStatus,
) {
    return axiosInstance.put<string>(
        `/api/v1/quote-templates/${quoteTemplateId}/template-forms/${templateFormId}/${status}`,
    );
}

export async function updateQuoteTemplatePricing(
    quoteTemplateId: number | string,
    payload: QuoteTemplatePricingUpdate,
) {
    return axiosInstance.put<QuoteTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/pricing`,
        payload,
    );
}

export async function getIntakeEmailTemplate(quoteTemplateId: number) {
    return axiosInstance.get<EmailTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/intake-email-template`,
    );
}

export async function getIntakeEmailReminderTemplate(quoteTemplateId: number) {
    return axiosInstance.get<EmailTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/intake-email-reminder-template`,
    );
}

export async function getQuoteEmailReminderTemplate(quoteTemplateId: number | string) {
    return axiosInstance.get<EmailTemplate>(
        `/api/v1/quote-templates/${quoteTemplateId}/quote-email-reminder-template`,
    );
}

export async function addDynamicPrices(
    quoteTemplateId: number | string,
    payload: DynamicFeesPayload[],
) {
    return axiosInstance.post<DynamicPriceType[]>(
        `/api/v1/quote-templates/${quoteTemplateId}/dynamic-prices`,
        payload,
    );
}

export async function getDynamicPrices(
    quoteTemplateId: number | string,
    dynamicPriceCategories: string[],
) {
    return axiosInstance.get<DynamicPriceType[]>(
        `/api/v1/quote-templates/${quoteTemplateId}/dynamic-prices`,
        {
            params: { dynamicPriceCategories },
            paramsSerializer: { indexes: null },
        },
    );
}

export async function patchDynamicPriceField(
    quoteTemplateDynamicPriceId: number,
    payload: Operation[],
) {
    return axiosInstance.patch<DynamicPriceType>(
        `/api/v1/quote-templates/${quoteTemplateDynamicPriceId}/dynamic-prices`,
        payload,
        { headers: { 'Content-Type': 'application/json-patch+json' } },
    );
}

export async function deleteDynamicPriceField(quoteTemplateDynamicPriceId: number) {
    return axiosInstance.delete<null>(
        `/api/v1/quote-templates/${quoteTemplateDynamicPriceId}/dynamic-prices`,
    );
}

export async function deleteQuoteTemplate(quoteTemplateId: number) {
    return axiosInstance.delete<null>(`/api/v1/quote-templates/${quoteTemplateId}`);
}
